
import React, { createContext, useReducer, useContext, useEffect } from 'react';
import type { ViewType } from '../types.ts';

// State shape
interface GlobalState {
  activeView: ViewType;
  // In a real app, you'd add more global state here
  // e.g., openFiles, terminalHistory, etc.
}

// Action types
type Action =
  | { type: 'SET_VIEW'; payload: ViewType };
  // Add other action types as needed

const initialState: GlobalState = {
  activeView: 'ai-feature-builder',
};

const reducer = (state: GlobalState, action: Action): GlobalState => {
  switch (action.type) {
    case 'SET_VIEW':
      return { ...state, activeView: action.payload };
    default:
      return state;
  }
};

const GlobalStateContext = createContext<{
  state: GlobalState;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => null,
});

const LOCAL_STORAGE_KEY = 'devcore_snapshot';

export const GlobalStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState, (initial) => {
        try {
            const storedState = localStorage.getItem(LOCAL_STORAGE_KEY);
            return storedState ? JSON.parse(storedState) : initial;
        } catch (error) {
            console.error("Failed to parse state from localStorage", error);
            return initial;
        }
    });

    useEffect(() => {
        // Debounce saving to localStorage to avoid performance issues
        const handler = setTimeout(() => {
            try {
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
            } catch (error) {
                console.error("Failed to save state to localStorage", error);
            }
        }, 500); // Save state 500ms after the last change
        
        return () => clearTimeout(handler);
    }, [state]);

    return (
        <GlobalStateContext.Provider value={{ state, dispatch }}>
            {children}
        </GlobalStateContext.Provider>
    );
};

export const useGlobalState = () => useContext(GlobalStateContext);
